import { ArrowRight, LockKeyhole, UserPlus } from "lucide-react";
import { FormEvent, useCallback, useEffect, useState } from "react";
import { api } from "../api/client";
import type { CostCenter, SessionUser } from "../types";

type LoginPageProps = {
  onLogin: (user: SessionUser) => void;
};

export function LoginPage({ onLogin }: LoginPageProps) {
  const [mode, setMode] = useState<"login" | "register">("login");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [costCenterId, setCostCenterId] = useState("");
  const [costCenters, setCostCenters] = useState<CostCenter[]>([]);
  const [costCentersLoading, setCostCentersLoading] = useState(false);
  const [costCentersError, setCostCentersError] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const loadCostCenters = useCallback(async () => {
    setCostCentersLoading(true);
    setCostCentersError("");

    try {
      const rows = await api.publicCostCenters();
      setCostCenters(rows);
      setCostCenterId((current) => (rows.some((center) => center.id_cc === current) ? current : ""));
      if (rows.length === 0) setCostCentersError("Nenhum Centro de Custo ativo foi encontrado.");
    } catch {
      setCostCenters([]);
      setCostCenterId("");
      setCostCentersError("Nao foi possivel carregar os Centros de Custo.");
    } finally {
      setCostCentersLoading(false);
    }
  }, []);

  useEffect(() => {
    if (mode === "register") {
      void loadCostCenters();
    }
  }, [mode, loadCostCenters]);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setLoading(true);
    setError("");
    try {
      if (mode === "register" && password !== confirmPassword) {
        setError("A confirmacao de senha nao confere.");
        setLoading(false);
        return;
      }
      if (mode === "register" && !costCenterId) {
        setError("Selecione um Centro de Custo.");
        setLoading(false);
        return;
      }

      const response =
        mode === "login"
          ? await api.login(email, password)
          : await api.register({ nome: name, email, centro_custo: costCenterId, password, confirmPassword });
      onLogin(response.user);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Nao foi possivel concluir o acesso.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="login-page">
      <section className="login-hero">
        <div className="login-copy">
          <div className="brand">
            <div className="brand-mark">GS</div>
            <div>
              <strong>Grupo Setup</strong>
              <span>Portal de Treinamentos</span>
            </div>
          </div>
          <h1>Treinamentos internos, progresso e avaliacoes em um so lugar.</h1>
          <p>
            Plataforma responsiva para computador e celular, com Google Planilhas como base inicial e
            Google Drive para materiais.
          </p>
        </div>
        <form className="login-card" onSubmit={handleSubmit}>
          <div className="login-icon">
            {mode === "login" ? <LockKeyhole size={22} /> : <UserPlus size={22} />}
          </div>
          <h2>{mode === "login" ? "Acessar plataforma" : "Criar conta"}</h2>
          <div className="auth-tabs" role="tablist" aria-label="Acesso">
            <button
              type="button"
              className={mode === "login" ? "active" : ""}
              onClick={() => {
                setMode("login");
                setError("");
              }}
            >
              Entrar
            </button>
            <button
              type="button"
              className={mode === "register" ? "active" : ""}
              onClick={() => {
                setMode("register");
                setError("");
              }}
            >
              Cadastrar
            </button>
          </div>
          {mode === "register" ? (
            <label>
              Nome
              <input value={name} onChange={(event) => setName(event.target.value)} required minLength={2} />
            </label>
          ) : null}
          <label>
            E-mail
            <input value={email} onChange={(event) => setEmail(event.target.value)} type="email" required />
          </label>
          {mode === "register" ? (
            <label>
              Centro de Custo
            <select
                value={costCenterId}
                onChange={(event) => setCostCenterId(event.target.value)}
                onFocus={() => {
                  if (!costCentersLoading && costCenters.length === 0) void loadCostCenters();
                }}
                required
                disabled={costCentersLoading || costCenters.length === 0}
              >
                <option value="">
                  {costCentersLoading
                    ? "Carregando centros..."
                    : costCenters.length === 0
                      ? "Nenhum Centro de Custo disponivel"
                      : "Selecione seu Centro de Custo"}
                </option>
                {costCenters.map((center) => (
                  <option key={center.id_cc} value={center.id_cc}>
                    {center.descricao ? `${center.descricao} (${center.nome_cc})` : center.nome_cc}
                  </option>
                ))}
              </select>
              {costCentersError ? <span className="field-hint">{costCentersError}</span> : null}
            </label>
          ) : null}
          <label>
            Senha
            <input
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              type="password"
              required
              minLength={mode === "register" ? 8 : undefined}
            />
          </label>
          {mode === "register" ? (
            <label>
              Confirmar senha
              <input
                value={confirmPassword}
                onChange={(event) => setConfirmPassword(event.target.value)}
                type="password"
                required
                minLength={8}
              />
            </label>
          ) : null}
          {error ? <div className="form-error">{error}</div> : null}
          <button className="primary-button" type="submit" disabled={loading}>
            {loading ? "Aguarde..." : mode === "login" ? "Entrar" : "Criar conta"}
            <ArrowRight size={18} />
          </button>
          {mode === "register" ? (
            <small>Os modulos serao liberados conforme o Centro de Custo selecionado.</small>
          ) : null}
        </form>
      </section>
    </div>
  );
}
